import orderBy from 'lodash/orderBy';
import React, { useMemo } from 'react';
import { ChartCard, generateChartColors, InfoTooltip, PieChart } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';

export const Index_AssetClassFragment = gql(/* GraphQL */ `
  fragment Index_AssetClassFragment on ForestryIndicators {
    assetClass {
      items {
        count
        label
        pct
      }
      isDataSuppressed
    }
  }
`);

type AssetClassProps = {
  className?: string;
  indicators: FragmentType<typeof Index_AssetClassFragment>;
};

export function AssetClass({ indicators, className }: AssetClassProps) {
  const { assetClass } = useFragment(Index_AssetClassFragment, indicators);

  const { title, subtitle, chartTooltip, getOptionLabel, getOptionTooltip, getOptionDisplayValue } =
    useChartTranslations('assetClass');

  const pieData = useMemo(() => {
    const colors = generateChartColors(assetClass.items.length);
    const sorted = orderBy(
      assetClass.items
        .filter((item) => !!item)
        .map((item, index) => ({
          label: getOptionLabel(item?.label),
          tooltipLabel: getOptionTooltip(item?.label),
          value: item?.pct,
          color: colors[index],
          displayValue: getOptionDisplayValue(item.count, {
            pct: item.pct.toLocaleString('en', { maximumFractionDigits: 1 }),
          }),
        })),
      ['value'],
      ['desc']
    );
    return sorted.map((x, i) => ({ ...x, color: colors[i] }));
  }, [assetClass.items]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <ChartCard.Title>
          {title}
          {chartTooltip && <InfoTooltip className="ml-1">{chartTooltip}</InfoTooltip>}
        </ChartCard.Title>
        {subtitle && <ChartCard.Subtitle>{subtitle}</ChartCard.Subtitle>}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={assetClass.isDataSuppressed}>
        <PieChart data={pieData} showLegend chartPosition="right" />
      </ChartCard.Body>
    </ChartCard>
  );
}
